import { ArrowRight, ChevronDown, Clock } from "lucide-react";
import { Page, usePublicConfig } from "@/components/SiteLayout";
import { BRAND, FAQ, LANDING } from "@/config/content";

const BuyButton = ({ cfg, id }) => {
  if (!cfg) return <div className="h-14" />;
  if (!cfg.checkout_url) {
    return (
      <div data-testid={`${id}-pending`} className="inline-flex min-h-[56px] items-center gap-2 rounded-full border border-dashed border-stone-400 px-6 text-sm text-stone-600">
        <Clock size={16} /> {LANDING.checkoutPending}
      </div>
    );
  }
  return (
    <a href={cfg.checkout_url} data-testid={id} className="btn-accent w-full sm:w-auto">
      {LANDING.cta} <ArrowRight size={18} />
    </a>
  );
};

const Hero = ({ cfg }) => (
  <section className="mx-auto grid max-w-5xl gap-10 px-5 pb-16 pt-8 sm:px-8 md:grid-cols-[1.25fr_1fr] md:pt-16">
    <div className="fade-up">
      <span className="eyebrow">{BRAND.product}</span>
      <h1 data-testid="landing-headline" className="mt-5 text-4xl font-medium leading-[1.08] text-stone-900 sm:text-5xl lg:text-6xl">
        {LANDING.headline}
      </h1>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">{LANDING.sub}</p>
      <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
        <BuyButton cfg={cfg} id="landing-buy-button" />
        <p data-testid="landing-price" className="whitespace-nowrap text-stone-700">
          <span className="font-display text-2xl text-stone-900">{cfg?.price || "R$ 47,00"}</span>
          <span className="ml-2 text-sm">pagamento único</span>
        </p>
      </div>
    </div>
    <div className="relative hidden md:block fade-up" style={{ animationDelay: "120ms" }}>
      <div className="grid grid-cols-2 gap-3">
        {["k7", "m2", "r9", "t4"].map((k, i) => (
          <img key={k} src={`/img/b1_${k}.jpg`} alt="" className={`aspect-square w-full rounded-2xl object-cover ${i % 2 ? "translate-y-6" : ""}`} />
        ))}
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="mx-auto max-w-5xl px-5 sm:px-8">
    <div className="grid gap-px overflow-hidden rounded-3xl border bg-stone-200 md:grid-cols-3">
      {LANDING.pillars.map((p, i) => (
        <div key={p.t} className="bg-[#fffdf9] p-7" data-testid={`landing-pillar-${i}`}>
          <span className="font-display text-sm text-[#b85d3b]">0{i + 1}</span>
          <h3 className="mt-3 text-xl text-stone-900">{p.t}</h3>
          <p className="mt-2 text-sm leading-relaxed text-stone-600">{p.d}</p>
        </div>
      ))}
    </div>
  </section>
);

const How = () => (
  <section className="mx-auto max-w-5xl px-5 py-20 sm:px-8">
    <span className="eyebrow">{LANDING.howTitle}</span>
    <ol className="mt-8 space-y-8">
      {LANDING.steps.map((s, i) => (
        <li key={s.t} className="grid gap-2 border-l-2 border-[#c88a2e] pl-6 md:grid-cols-[260px_1fr] md:gap-10">
          <h3 className="text-xl text-stone-900">{i + 1}. {s.t}</h3>
          <p className="text-stone-600 leading-relaxed">{s.d}</p>
        </li>
      ))}
    </ol>
  </section>
);

const Faq = () => (
  <section className="mx-auto max-w-5xl px-5 pb-20 sm:px-8" data-testid="landing-faq">
    <span className="eyebrow">Perguntas frequentes</span>
    <div className="mt-6 divide-y divide-stone-200 border-y border-stone-200">
      {FAQ.map(([q, a], i) => (
        <details key={q} className="group py-5" data-testid={`faq-item-${i}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg text-stone-900 font-display">
            {q}
            <ChevronDown size={18} className="shrink-0 text-stone-500 transition-transform group-open:rotate-180" />
          </summary>
          <p className="mt-3 max-w-3xl leading-relaxed text-stone-600">{a}</p>
        </details>
      ))}
    </div>
  </section>
);

const Closing = ({ cfg }) => (
  <section className="mx-auto max-w-5xl px-5 sm:px-8">
    <div className="rounded-3xl bg-[#1c1917] p-8 text-stone-100 sm:p-12">
      <h2 className="max-w-2xl text-3xl leading-tight text-white sm:text-4xl">{BRAND.concept}</h2>
      <p className="mt-4 max-w-2xl text-stone-300">{LANDING.individual}</p>
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <BuyButton cfg={cfg} id="landing-buy-button-bottom" />
        <span className="text-sm text-stone-400">{cfg?.price || "R$ 47,00"} · {LANDING.priceNote}</span>
      </div>
      <p className="mt-10 border-t border-stone-700 pt-6 text-xs leading-relaxed text-stone-400" data-testid="landing-disclaimer">{LANDING.disclaimer}</p>
    </div>
  </section>
);

export default function Landing() {
  const cfg = usePublicConfig();
  return (
    <Page classicLogo>
      <Hero cfg={cfg} />
      <Pillars />
      <How />
      <Faq />
      <Closing cfg={cfg} />
    </Page>
  );
}
